import React, { createContext, useContext, useState, useEffect } from 'react';
import Button from 'components/ui/button';
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'components/ui/modal';
import { useApplication } from 'hooks/use_application';
import set from 'lodash/set';
import isNil from 'lodash/isNil';
import get from 'lodash/get';
import './json_form_editor.scss';
import { useStep } from 'components/wizard/context/step/hook';
import { updateBlock } from 'requests/blocks';
import { usePaul } from 'components/paul/provider';
import { useRequest } from 'components/wizard/context/request/hook';
import Icon from 'components/ui/icon';
import SchemaInput from 'components/ui/form/schema_input';

const getValueByPath = (obj, searchPath) => {
  const res = [];
  const find = (obj, searchPath, results = []) => {
    const pathToSearch = searchPath.startsWith('.') ? searchPath.slice(1) : searchPath;
    if (isNil(obj)) {
      return results;
    }
    if (typeof obj === 'object') {
      Object.entries(obj).forEach(([key, value]) => {
        if (pathToSearch.startsWith(key)) {
          results.push({ key, value });
          find(value, pathToSearch.slice(key.length), results);
        }
      });
    }
    if (Array.isArray(obj)) {
      obj.forEach((value, index) => {
        if (pathToSearch.startsWith(`[${index}]`)) {
          results.push({ key: index, value });
          find(value, pathToSearch.slice(`[${index}]`.length), results);
        }
      });
    }
    return results;
  };
  find(obj, searchPath, res);
  return res[res.length - 1].value;
};

const JsonFormEditorContext = createContext({});

export const useJsonFormEditor = () => useContext(JsonFormEditorContext);

const JsonFormEditor = ({ schema, children }) => {
  const { debugJsonForm } = useApplication();
  const [errors, setErrors] = useState(null);
  const { request, translations } = useRequest();
  const [availableData, setAvailableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { reloadWorkflow } = usePaul();
  const [schemaToEdit, setSchemaToEdit] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { step } = useStep();
  const { listAvailableData } = usePaul();

  useEffect(() => {
    if (!debugJsonForm) return;
    listAvailableData(request.workflow_id)
      .then((data) => {
        setAvailableData(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }, [debugJsonForm]);

  const onSetToSchema = (path, field, slug) => {
    const block = step.blocks.find((block) => block.slug === slug);
    const schema = JSON.parse(JSON.stringify(block.form_schema.schema));
    set(schema, path, field);
    updateBlock(block.id, { form_schema: { schema } }).then((response) => {
      reloadWorkflow();
      closeModal();
    });
  };

  const onEdit = (path, field, slug) => {
    const block = step.blocks.find((block) => block.slug === slug);
    const schema = block.form_schema.schema;
    setSchemaToEdit({
      slug,
      path,
      field: getValueByPath(schema, path),
    });
    setShowModal(true);
  };

  const saveSchema = () => {
    const block = step.blocks.find((block) => block.slug === schemaToEdit.slug);
    const schema = JSON.parse(JSON.stringify(block.form_schema.schema));
    set(schema, schemaToEdit.path, schemaToEdit.field);
    updateBlock(block.id, { form_schema: { schema } }).then((response) => {
      reloadWorkflow();
      closeModal();
    });
  };

  const closeModal = () => {
    setSchemaToEdit(null);
    setShowModal(false);
  };

  if (!debugJsonForm) {
    return children;
  }

  return (
    <>
      <JsonFormEditorContext.Provider value={{ onSetToSchema, onEdit, editor: true }}>{children}</JsonFormEditorContext.Provider>
      {schemaToEdit && !loading && (
        <Modal isOpen={showModal} size="xxl">
          <ModalHeader toggle={closeModal}>
            <p className="d-flex align-center mb-0">
              <code className="me-2">{schemaToEdit.slug}</code>
              <Icon className="me-2" name="arrow-right" />
              <code className="me-2">{schemaToEdit.path}</code>
            </p>
          </ModalHeader>
          <ModalBody className="p-2 form-editor-modal">
            <SchemaInput
              input={{
                value: schemaToEdit.field,
                onChange: (schema) => {
                  setErrors(null);
                  setSchemaToEdit({
                    ...schemaToEdit,
                    field: schema,
                  });
                },
              }}
              onError={(errors) => {
                setErrors(errors);
              }}
              availableData={availableData}
              availableTranslations={translations}
            />
          </ModalBody>
          <ModalFooter>
            {errors && <p className="text-danger">{errors}</p>}
            <Button disabled={!isNil(errors)} onClick={saveSchema}>
              Save
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default JsonFormEditor;
